import React, { useState, useEffect, useCallback } from 'react';
import { WIPMode } from '@punchcard/core/constants/Wip';

interface UseSymbolSelectorModalParams {
	mode: WIPModeType;
}

interface UseSymbolSelectorModalReturn {
	showSymbolsModal: boolean;
	activeTextAreaElement: HTMLTextAreaElement | null;
	handleSymbolSelect: (symbol: string) => void;
	toggleSymbolsModal: (textAreaRef: React.RefObject<HTMLTextAreaElement>) => void;
	closeModal: () => void;
}

/**
 * Custom hook to manage symbol selector modal state and interactions
 */
export const useSymbolSelectorModal = ({ mode }: UseSymbolSelectorModalParams): UseSymbolSelectorModalReturn => {
	const [showSymbolsModal, setShowSymbolsModal] = useState<boolean>(false);
	const [activeTextAreaElement, setActiveTextAreaElement] = useState<HTMLTextAreaElement | null>(null);

	const handleSymbolSelect = useCallback((symbol: string) => {
		if (activeTextAreaElement) {
			// Trigger custom event that TextAreaContainer will listen for
			const event = new CustomEvent('symbolSelected', { detail: { symbol } });
			activeTextAreaElement.dispatchEvent(event);
		}
	}, [activeTextAreaElement]);

	const closeModal = useCallback(() => {
		setShowSymbolsModal(false);
		setActiveTextAreaElement(null);
	}, []);

	const toggleSymbolsModal = useCallback((textAreaRef: React.RefObject<HTMLTextAreaElement>) => {
		const textAreaElement = textAreaRef.current;
		if (!textAreaElement) {
			return;
		}

		if (showSymbolsModal && activeTextAreaElement === textAreaElement) {
			// If the modal is open for this textarea, close it
			closeModal();
		} else {
			// Close existing modal if opening for a different textarea
			if (showSymbolsModal && activeTextAreaElement !== textAreaElement) {
				closeModal();
			}
			// Open modal for this textarea
			setActiveTextAreaElement(textAreaElement);
			setShowSymbolsModal(true);
		}
	}, [showSymbolsModal, activeTextAreaElement, closeModal]);

	// Close symbols modal when mode changes away from Type
	useEffect(() => {
		if (showSymbolsModal && mode !== WIPMode.Type) {
			closeModal();
		}
	}, [mode, showSymbolsModal, closeModal]);

	return {
		showSymbolsModal,
		activeTextAreaElement,
		handleSymbolSelect,
		toggleSymbolsModal,
		closeModal
	};
};
